'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Viewport = { width: number; height: number };
type PdfPage = {
  getViewport: (p: { scale: number }) => Viewport;
  render: (p: {
    canvasContext: CanvasRenderingContext2D;
    canvas: HTMLCanvasElement;
    viewport: Viewport;
  }) => {
    promise: Promise<void>;
    cancel: () => void;
  };
  getTextContent: () => Promise<{ items: Array<{ str?: string }> }>;
};
type PdfDoc = {
  numPages: number;
  getPage: (n: number) => Promise<PdfPage>;
  destroy?: () => Promise<void> | void;
};
type PdfJs = {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument: (p: { data: Uint8Array; isEvalSupported?: boolean }) => { promise: Promise<PdfDoc> };
};

let pdfjs: Promise<PdfJs> | null = null;
function loadPdfJs() {
  if (!pdfjs) pdfjs = import('pdfjs-dist/legacy/build/pdf.mjs') as unknown as Promise<PdfJs>;
  return pdfjs.then((m) => {
    if (!m.GlobalWorkerOptions.workerSrc) m.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';
    return m;
  });
}

function Page({
  doc,
  n,
  width,
  zoom,
  night,
  onVisible,
}: {
  doc: PdfDoc;
  n: number;
  width: number;
  zoom: number;
  night: boolean;
  onVisible: (n: number) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [ratio, setRatio] = useState(1.294); // A4/Letter-ish until the page tells us
  const [near, setNear] = useState(n <= 2);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setNear(true);
        if (e.intersectionRatio > 0.5) onVisible(n);
      },
      { rootMargin: '600px 0px', threshold: [0, 0.5] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [n, onVisible]);

  useEffect(() => {
    if (!near) return;
    let task: { cancel: () => void } | null = null;
    let dead = false;
    void doc.getPage(n).then((page) => {
      if (dead || !canvas.current) return;
      const base = page.getViewport({ scale: 1 });
      setRatio(base.height / base.width);
      const cssW = width * zoom;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const vp = page.getViewport({ scale: (cssW / base.width) * dpr });
      const c = canvas.current;
      c.width = Math.round(vp.width);
      c.height = Math.round(vp.height);
      const ctx = c.getContext('2d');
      if (!ctx) return;
      const t = page.render({ canvasContext: ctx, canvas: c, viewport: vp });
      task = t;
      t.promise.catch(() => undefined);
    });
    return () => {
      dead = true;
      task?.cancel();
    };
  }, [doc, n, width, zoom, near]);

  const cssW = width * zoom;
  return (
    <div
      data-page={n}
      ref={box}
      style={{
        width: cssW,
        height: cssW * ratio,
        margin: '0 auto 12px',
        background: '#fff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        position: 'relative',
      }}
    >
      <canvas
        aria-label={`Page ${n}`}
        ref={canvas}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          filter: night ? 'invert(0.9) hue-rotate(180deg)' : 'none',
        }}
      />
      <span style={{ position: 'absolute', right: 6, bottom: 4, fontSize: 11, color: '#888' }}>
        {n}
      </span>
    </div>
  );
}

export default function BrowserPdfReader({ config }: { config?: { toolSlug?: string } }) {
  const [doc, setDoc] = useState<PdfDoc | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [night, setNight] = useState(false);
  const [current, setCurrent] = useState(1);
  const [width, setWidth] = useState(800);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<number[] | null>(null);
  const [drag, setDrag] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const texts = useRef<Map<number, string>>(new Map());

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) =>
      setWidth(Math.max(260, Math.min(900, e.contentRect.width - 24))),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, [doc]);

  const open = async (file: File) => {
    setError(null);
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      setError('That is not a PDF file.');
      return;
    }
    setLoading(true);
    try {
      const m = await loadPdfJs();
      const d = await m.getDocument({
        data: new Uint8Array(await file.arrayBuffer()),
        isEvalSupported: false,
      }).promise;
      void doc?.destroy?.();
      texts.current = new Map();
      setHits(null);
      setDoc(d);
      setName(file.name);
      setCurrent(1);
      setZoom(1);
      void consumeToolUsage(config?.toolSlug ?? 'pdf-reader', '').catch(() => undefined);
    } catch (e) {
      const msg =
        (e as Error)?.name === 'PasswordException'
          ? 'This PDF is password-protected.'
          : 'The PDF could not be opened. It may be damaged.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const goTo = (n: number) => {
    const el = scroller.current?.querySelector(`[data-page="${n}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const search = async () => {
    if (!doc || !query.trim()) return;
    const q = query.trim().toLowerCase();
    const found: number[] = [];
    for (let i = 1; i <= doc.numPages; i += 1) {
      if (!texts.current.has(i)) {
        const page = await doc.getPage(i);
        const tc = await page.getTextContent();
        texts.current.set(
          i,
          tc.items
            .map((it) => it.str ?? '')
            .join(' ')
            .toLowerCase(),
        );
      }
      if (texts.current.get(i)?.includes(q)) found.push(i);
    }
    setHits(found);
    if (found.length) goTo(found[0]);
  };

  const onVisible = useCallback((n: number) => setCurrent(n), []);

  if (!doc) {
    return (
      <div className="tool-live-shell">
        <div className="tool-live-header">
          <span className="tool-live-badge">PDF</span>
          <h2 className="tool-live-title">PDF reader</h2>
          <p className="tool-live-subtitle">
            Open a PDF to read it here. The file stays on your device; nothing is uploaded.
          </p>
        </div>
        <label
          onDragLeave={() => setDrag(false)}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const f = e.dataTransfer.files[0];
            if (f) void open(f);
          }}
          style={{
            display: 'grid',
            placeItems: 'center',
            minHeight: 220,
            border: `2px dashed ${drag ? 'var(--accent, #2563eb)' : 'currentColor'}`,
            borderRadius: 12,
            cursor: 'pointer',
            textAlign: 'center',
            padding: '1rem',
          }}
        >
          <input
            accept="application/pdf,.pdf"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void open(f);
            }}
            style={{ display: 'none' }}
            type="file"
          />
          <span>
            <strong>{loading ? 'Opening…' : 'Choose a PDF'}</strong>
            <br />
            or drop it here
          </span>
        </label>
        {error ? <p className="status-message status-error">{error}</p> : null}
      </div>
    );
  }

  return (
    <div className="tool-live-shell">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 2,
          background: 'var(--surface, var(--bg, #fff))',
          padding: '0.5rem 0',
        }}
      >
        <strong
          style={{
            flex: '1 1 160px',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </strong>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          <input
            aria-label="Page number"
            inputMode="numeric"
            onChange={(e) => {
              const n = Number(e.target.value);
              if (n >= 1 && n <= doc.numPages) goTo(n);
            }}
            style={{ width: '3.5rem' }}
            type="number"
            value={current}
          />{' '}
          / {doc.numPages}
        </span>
        <button
          className="button button-secondary"
          onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
          type="button"
        >
          −
        </button>
        <span style={{ minWidth: '3rem', textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
        <button
          className="button button-secondary"
          onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}
          type="button"
        >
          +
        </button>
        <button
          className="button button-secondary"
          onClick={() => setNight((v) => !v)}
          type="button"
        >
          {night ? 'Day' : 'Night'}
        </button>
        <label className="button button-secondary" style={{ cursor: 'pointer' }}>
          Open another
          <input
            accept="application/pdf,.pdf"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void open(f);
            }}
            style={{ display: 'none' }}
            type="file"
          />
        </label>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void search();
        }}
        style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}
      >
        <input
          aria-label="Search in the PDF"
          onChange={(e) => {
            setQuery(e.target.value);
            setHits(null);
          }}
          placeholder="Search text"
          style={{ flex: '1 1 180px' }}
          type="search"
          value={query}
        />
        <button className="button button-secondary" type="submit">
          Find
        </button>
        {hits ? (
          <span className="tool-live-panel-note" style={{ margin: 0 }}>
            {hits.length ? (
              <>
                On page{hits.length > 1 ? 's' : ''}{' '}
                {hits.slice(0, 20).map((h) => (
                  <button
                    className="button button-secondary"
                    key={h}
                    onClick={() => goTo(h)}
                    style={{ padding: '0.1rem 0.45rem', marginRight: 4 }}
                    type="button"
                  >
                    {h}
                  </button>
                ))}
              </>
            ) : (
              'Not found. Scanned PDFs contain images, not text; use OCR PDF to Text.'
            )}
          </span>
        ) : null}
      </form>
      <div
        ref={scroller}
        style={{
          maxHeight: '80vh',
          overflow: 'auto',
          background: night ? '#1f2125' : 'rgba(127,127,127,0.12)',
          borderRadius: 8,
          padding: 12,
        }}
      >
        {Array.from({ length: doc.numPages }, (_, i) => (
          <Page
            doc={doc}
            key={`${name}-${i}`}
            n={i + 1}
            night={night}
            onVisible={onVisible}
            width={width}
            zoom={zoom}
          />
        ))}
      </div>
    </div>
  );
}
